import React, { useState } from "react"
import { submitClue } from "./justOneRoom"
import { Screen, JoButton, PlayerDot, C, F } from "./JustOneUI"
import { playSound } from "../../../utils/ballparkSounds"

const ClueScreen = ({ room, code, playerId }) => {
  const [clue, setClue] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const secretWord = room.secretWord || ""
  const isGuesser = room.guesserId === playerId
  const guesserName = room.players?.[room.guesserId]?.name || "The Guesser"

  const players = Object.entries(room.players || {}).sort(
    (a, b) => a[1].joinedAt - b[1].joinedAt
  )

  const handleSubmit = async () => {
    const trimmed = clue.trim()
    if (!trimmed) return

    // Standard rule validation: Just one word (allow hyphenated like spider-man, but no spaces)
    if (trimmed.includes(" ")) {
      setError("Your clue must be exactly ONE word (no spaces)!")
      return
    }

    setBusy(true)
    setError("")
    try {
      playSound("click")
      await submitClue(code, room.round, playerId, trimmed)
    } catch (e) {
      setError("Failed to submit. Try again.")
      setBusy(false)
    }
  }

  // Check if I have already submitted my clue
  const myClueSubmitted = !!room.clues?.[playerId]

  return (
    <Screen style={{ gap: 16 }}>
      {/* Header bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.muted }}>
          ROUND {room.round} OF {room.totalRounds}
        </div>
        <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 16, color: C.teal }}>
          TEAM SCORE: {room.teamScore || 0}
        </div>
      </div>

      {isGuesser ? (
        // GUESSER SCREEN
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 24, textAlign: "center" }}>
          <div
            style={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              border: `4px solid ${C.cyan}`,
              borderTopColor: "transparent",
              animation: "spin 1s linear infinite",
            }}
          />
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}} />
          <div>
            <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 28, color: C.cyan, marginBottom: 8 }}>
              You are the Guesser!
            </div>
            <div style={{ fontSize: 16, color: C.muted, fontWeight: 700, maxWidth: 360, margin: "0 auto" }}>
              Clue givers are writing their one-word clues. Sit tight!
            </div>
          </div>

          <div style={{ width: "100%", marginTop: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.5, color: C.muted, fontFamily: F.display, textAlign: "left", marginBottom: 8 }}>
              CLUE STATUS
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {players
                .filter(([pid]) => pid !== room.guesserId)
                .map(([pid, p]) => {
                  const hasSubmitted = !!room.clues?.[pid]
                  return (
                    <div
                      key={pid}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        background: C.card,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 12,
                        padding: "10px 14px",
                        opacity: hasSubmitted ? 1 : 0.6,
                      }}
                    >
                      <PlayerDot color={p.color} />
                      <span style={{ fontWeight: 800, fontSize: 15, color: C.ink }}>{p.name}</span>
                      <span style={{ marginLeft: "auto", fontWeight: 900, color: hasSubmitted ? C.green : C.muted }}>
                        {hasSubmitted ? "READY ✓" : "WRITING…"}
                      </span>
                    </div>
                  )
                })}
            </div>
          </div>
        </div>
      ) : (
        // CLUE GIVER SCREEN
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ textAlign: "center", padding: "16px 8px", background: C.highlight, border: `2.5px dashed ${C.cyan}`, borderRadius: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 1, color: C.cyanDark, fontFamily: F.display, marginBottom: 4 }}>
              SECRET WORD FOR {guesserName.toUpperCase()}
            </div>
            <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 44, color: C.ink, textTransform: "uppercase", letterSpacing: 1 }}>
              {secretWord}
            </div>
          </div>

          {myClueSubmitted ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", gap: 16, textAlign: "center" }}>
              <div style={{ fontSize: 48 }}>📬</div>
              <div>
                <div style={{ fontFamily: F.display, fontWeight: 900, fontSize: 20, color: C.green, marginBottom: 4 }}>
                  Clue Submitted!
                </div>
                <div style={{ fontSize: 14, color: C.muted, fontWeight: 700 }}>
                  Waiting for other clue givers to finish...
                </div>
              </div>
              
              <div style={{ width: "100%", marginTop: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {players
                    .filter(([pid]) => pid !== room.guesserId)
                    .map(([pid, p]) => {
                      const hasSubmitted = !!room.clues?.[pid]
                      return (
                        <div
                          key={pid}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            background: C.card,
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 12,
                            padding: "8px 12px",
                            opacity: hasSubmitted ? 1 : 0.6,
                          }}
                        >
                          <PlayerDot color={p.color} size={18} />
                          <span style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>
                            {p.name} {pid === playerId && " (You)"}
                          </span>
                          <span style={{ marginLeft: "auto", fontWeight: 800, color: hasSubmitted ? C.green : C.muted, fontSize: 13 }}>
                            {hasSubmitted ? "Submitted ✓" : "Writing…"}
                          </span>
                        </div>
                      )
                    })}
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.muted, lineHeight: 1.4 }}>
                Write <strong style={{ color: C.ink }}>Just One</strong> word to help them guess the secret word.
                <br />
                <span style={{ fontSize: 13, color: C.coral }}>
                  ⚠️ If your clue matches anyone else's clue, both will be eliminated!
                </span>
              </div>

              <input
                style={{
                  height: 52,
                  borderRadius: 12,
                  border: `1.5px solid ${C.borderDark}`,
                  background: C.card,
                  padding: "0 16px",
                  fontSize: 18,
                  fontWeight: 700,
                  fontFamily: F.body,
                  color: C.ink,
                  width: "100%",
                  boxSizing: "border-box",
                  outline: "none",
                }}
                placeholder="Enter one word..."
                value={clue}
                maxLength={24}
                onChange={(e) => {
                  setClue(e.target.value.replace(/\s+/g, "")) // Remove space
                  setError("")
                }}
                disabled={busy}
              />

              {error && (
                <div style={{ color: C.coral, fontWeight: 700, fontSize: 14, textAlign: "center" }}>
                  {error}
                </div>
              )}

              <JoButton onClick={handleSubmit} disabled={busy || !clue.trim()}>
                {busy ? "Submitting…" : "Submit Clue"}
              </JoButton>
            </div>
          )}
        </div>
      )}
    </Screen>
  )
}

export default ClueScreen
